import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar, useLang } from '../../../i18n/cda';
import { CheckCircle, ArrowRight, Clock, Users, Award, Briefcase } from 'lucide-react';

const CdaCareerCoachingSanAntonio = () => {
  const navigate = useNavigate();
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>CDA Career Coaching in San Antonio, Texas | Care Comply</title>
        <meta name="description" content="Get personalized CDA career coaching in San Antonio, Texas. Care Comply helps aspiring early childhood educators complete their Child Development Associate credential with expert guidance." />
        <link rel="canonical" href="https://carecomplycda.com/cda-career-coaching-san-antonio" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Start Your Journey
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">San Antonio, Texas</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CDA Career Coaching in San Antonio
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Personalized guidance to help you earn your Child Development Associate credential and launch your early childhood education career in San Antonio and South Texas.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">What is CDA Career Coaching?</h2>
          <p className="text-slate-600 mb-4">
            The Child Development Associate (CDA) credential is the most recognized certification in early childhood education. However, earning it requires completing 120 hours of professional education, 480 hours of hands-on experience, building a professional portfolio, and passing an observation.
          </p>
          <p className="text-slate-600 mb-4">
            Many aspiring educators in San Antonio struggle to navigate this process alone. That's where Care Comply comes in. Founded by Dr. Gabriela Ibarra-Noriega, Care Comply provides step-by-step career coaching specifically designed for CDA candidates in Texas.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Clock className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Flexible Online Support</h3>
            <p className="text-slate-600">Access your CDA coaching anytime from San Antonio or anywhere in Texas. Get guidance when you need it.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Users className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Local Center Connections</h3>
            <p className="text-slate-600">We partner with childcare centers in San Antonio to help you find placement for your required 480 hours.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Award className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Portfolio Guidance</h3>
            <p className="text-slate-600">Build your professional portfolio with templates, checklists, and expert feedback.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Briefcase className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Job Readiness Training</h3>
            <p className="text-slate-600">Prepare for interviews, build your resume, and develop workplace skills employers want.</p>
          </div>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Ready to Start Your CDA Journey in San Antonio?</h2>
          <p className="mb-6 opacity-90">Join hundreds of aspiring educators who have completed their CDA with Care Comply.</p>
          <Button onClick={() => navigate('/cda')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Get Started Today <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply serves San Antonio, Austin, Houston, Dallas, and all of Texas.</p>
          <p className="mt-2">
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline">480-Hour Support</Link> • 
            <Link to="/cda-portfolio-help" className="text-[#0d7d6f] hover:underline ml-2">Portfolio Help</Link> • 
            <Link to="/cda-job-readiness" className="text-[#0d7d6f] hover:underline ml-2">Job Readiness</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CdaCareerCoachingSanAntonio;
